'use client'

import { createClient } from '@/lib/supabase/client'
import { authErrorText } from '@/lib/auth-error'
import { useCallback, useEffect, useRef, useState } from 'react'
import { isIos, isStandalone } from '@/lib/pwa'
import { HANDOFF_TTL_MS, handoffId, newHandoffSecret } from '@/lib/pwa-handoff'

// Установленное приложение на iPhone: iOS может завершить вход через Google во
// внешнем Safari с его собственными cookies, и приложение осталось бы гостем.
// Поэтому там вход идёт во внешнем окне (app/pwa-login/start), а приложение
// опрашивает сервер и получает свою сессию (app/api/auth/pwa-handoff).
// Секрет попытки — в sessionStorage, чтобы пережить перезагрузку страницы.
const HANDOFF_KEY = 'tk-pwa-handoff'
type Handoff = { secret: string; id: string; startedAt: number }

function readPending(): Handoff | null {
  try {
    const h = JSON.parse(sessionStorage.getItem(HANDOFF_KEY) ?? 'null') as Handoff | null
    return h && Date.now() - h.startedAt < HANDOFF_TTL_MS ? h : null
  } catch {
    return null
  }
}

function savePending(h: Handoff | null) {
  try {
    if (h) sessionStorage.setItem(HANDOFF_KEY, JSON.stringify(h))
    else sessionStorage.removeItem(HANDOFF_KEY)
  } catch {}
}

// blockedReason — текст, если кнопкой сейчас пользоваться нельзя. На
// регистрации так требуется согласие на обработку данных: галочку в форме
// кнопка Google иначе обходила бы (152-ФЗ, .scratch/legal/01-audit-2026-09-19.md).
// consent — отмеченные галочки. Их несёт адрес коллбэка: у входа через Google
// нет метаданных формы, и без этого согласие не дошло бы до журнала.
// notice — чем закончилась прошлая попытка входа через Google (lib/oauth-error.ts).
// provider — Google (OAuth через GoTrue) или Яндекс ID (свои маршруты
// app/auth/yandex, см. lib/yandex-id.ts). Передача входа в приложение на
// iPhone у обоих одна: Яндекс после входа сам ведёт на /pwa-login/done?h=.
export function GoogleAuthButton({
  blockedReason = null,
  consent = null,
  notice = null,
  provider = 'google',
  next = null,
}: {
  blockedReason?: string | null
  consent?: { marketing: boolean } | null
  notice?: string | null
  provider?: 'google' | 'yandex'
  // Куда вернуть после входа (например, в часть 3 урока со стены гостя,
  // components/guest-wall.tsx); коллбэки проверяют его через safeNext.
  next?: string | null
}) {
  const label = provider === 'yandex' ? 'Яндекс ID' : 'Google'
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(notice)
  // Режим установленного приложения на iPhone: заготовленная попытка (id
  // считается асинхронно, а окно нужно открыть прямо в обработчике нажатия)
  // и признак «ждём подтверждения из внешнего окна».
  const prepared = useRef<Handoff | null>(null)
  const [pwaMode, setPwaMode] = useState(false)
  const [waiting, setWaiting] = useState(false)

  const claim = useCallback(async () => {
    const h = readPending()
    if (!h) {
      setWaiting(false)
      return
    }
    try {
      const res = await fetch('/api/auth/pwa-handoff', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ secret: h.secret }),
      })
      if (res.status === 200) {
        savePending(null)
        // Полная загрузка: сессия пришла cookies ответа, серверу её нужно увидеть.
        // eslint-disable-next-line @next/next/no-location-assign-relative-destination
        window.location.href = '/dashboard'
      }
    } catch {}
  }, [])

  useEffect(() => {
    if (!isIos() || !isStandalone()) return
    let cancelled = false
    const secret = newHandoffSecret()
    handoffId(secret).then((id) => {
      if (cancelled) return
      prepared.current = { secret, id, startedAt: 0 }
      setPwaMode(true)
      if (readPending()) setWaiting(true)
    })
    return () => {
      cancelled = true
    }
  }, [])

  // Пока ждём — опрашиваем каждые 2 с и сразу при возврате в приложение.
  useEffect(() => {
    if (!waiting) return
    const timer = setInterval(claim, 2000)
    const onVisible = () => {
      if (document.visibilityState === 'visible') claim()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [waiting, claim])

  const handleClick = async () => {
    if (blockedReason) {
      setError(blockedReason)
      return
    }
    setError(null)
    const qs = new URLSearchParams()
    if (consent) {
      qs.set('consent', '1')
      if (consent.marketing) qs.set('marketing', '1')
    }
    if (next) qs.set('next', next)
    const consentParams = qs.size ? `?${qs.toString()}` : ''

    if (pwaMode && prepared.current) {
      const h = { ...prepared.current, startedAt: Date.now() }
      savePending(h)
      const start = provider === 'yandex' ? '/auth/yandex/start' : '/pwa-login/start'
      const url = `${start}?h=${h.id}${consentParams.replace('?', '&')}`
      // Окно не открылось — идём в том же: если iOS вернёт вход сюда же,
      // /pwa-login/done сам поймёт, что он в приложении.
      // /pwa-login/start — route handler, не страница Next: нужен настоящий переход.
      // eslint-disable-next-line @next/next/no-location-assign-relative-destination
      if (!window.open(url, '_blank')) window.location.href = url
      setWaiting(true)
      return
    }

    setIsLoading(true)
    if (provider === 'yandex') {
      // Маршрут сам ставит cookie состояния и уводит к Яндексу.
      // eslint-disable-next-line @next/next/no-location-assign-relative-destination
      window.location.href = `/auth/yandex/start${consentParams}`
      return
    }
    const supabase = createClient()
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/oauth/callback${consentParams}`,
      },
    })

    if (error) {
      setError(authErrorText(error))
      setIsLoading(false)
    }
    // При успехе браузер уходит на Google — код ниже не выполнится.
  }

  if (waiting) {
    return (
      <div className="flex flex-col gap-2 text-center">
        <p className="text-[15px] leading-relaxed">
          Завершите вход в открывшемся окне и нажмите там &quot;Войти в приложение&quot;. Потом вернитесь сюда - вход произойдёт сам.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <button type="button" onClick={claim} className="tk-pill">
            Проверить вход
          </button>
          <button
            type="button"
            onClick={() => {
              savePending(null)
              setWaiting(false)
            }}
            className="tk-pill"
          >
            Отмена
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={handleClick}
        disabled={isLoading}
        className="tk-glass-btn tk-glass-ice w-full rounded-2xl px-4 py-3 text-sm font-bold disabled:opacity-50"
      >
        {isLoading ? `Открываем ${label}...` : `Продолжить с ${label}`}
      </button>
      {error && <p className="text-sm" style={{ color: 'oklch(0.7 0.19 25)' }}>{error}</p>}
    </div>
  )
}
